import { useEffect } from "react"
import { App } from "@capacitor/app"
import { Call, VibratoClient } from "@/lib/vibrato-api"
import { settings } from "@/settings"

export default function useRefreshCallOnResume(
  client: VibratoClient,
  callId: number | null | undefined,
  refreshCallback: (call: Call) => void
) {
  // Only run in native app
  if (!settings.IS_NATIVE_APP) {
    return
  }

  useEffect(() => {
    if (!client || !callId) {
      return
    }

    let lastKnownStatus: string | null = null
    let isCallCompleted = false
    let resumeListener: any = null

    // Get initial call status
    const fetchInitialCallStatus = async () => {
      try {
        const call = await client.getCall(callId)
        lastKnownStatus = call.status
        isCallCompleted = call.status === "completed"
        console.log("App: initial call status set", lastKnownStatus)
      } catch (error) {
        console.error("Failed to fetch initial call status", error)
      }
    }

    fetchInitialCallStatus()

    const setupResumeListener = async () => {
      if (isCallCompleted) {
        return
      }

      // Setup app resume listener
      resumeListener = await App.addListener("resume", async () => {
        console.log("App: resumed in useRefreshCallOnResume")

        // Don't refresh if call is already completed
        if (isCallCompleted) {
          return
        }

        try {
          // Check if call status has changed
          const currentCall = await client.getCall(callId)
          const currentStatus = currentCall.status

          console.log("App: comparing call status", {
            lastKnownStatus,
            currentStatus
          })

          // Update isCallCompleted flag if status is now "completed"
          if (currentStatus === "completed") {
            isCallCompleted = true
          }

          if (lastKnownStatus && currentStatus !== lastKnownStatus) {
            console.log("App: call status changed, refreshing", {
              from: lastKnownStatus,
              to: currentStatus
            })
            lastKnownStatus = currentStatus
            refreshCallback(currentCall)
          } else {
            console.log("App: call status unchanged, no refresh needed")
          }
        } catch (error) {
          console.log("App: error checking call status", error)
        }
      })
    }

    setupResumeListener()

    return () => {
      // Cleanup app resume listener
      if (resumeListener) {
        resumeListener.remove()
        console.debug("App: resume listener removed in useRefreshCallOnResume")
      }
    }
  }, [client, callId, refreshCallback])
}
